import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { requireMinOneColleagueValidator } from 'src/app/validators/at-least-one-colleague';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/services/notification.service';
import { ToastrService } from 'ngx-toastr';
import { Contract, FileUpload, User } from '../../contracte-programari.interface';
import { RequestMessageInternPayload, RequestMessageNotitaPayload } from './mesagerie.interface';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, map, startWith, Subscription } from 'rxjs';
import { BeneficiariService } from '../../../beneficiari/beneficiari.service';
import { helper } from 'src/environments/helper';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { AppFilesService } from 'src/app/services/app-files.service';
import { UsersService } from 'src/app/services/users.service';
import { ContracteProgramariService } from '../../contracte-programari.service';


@Component({
    selector: 'app-mesagerie',
    templateUrl: './mesagerie.component.html',
    styleUrls: ['./mesagerie.component.scss']
})
export class MesagerieComponent implements OnInit {
    @Input() contract!: Contract;
    @Output() contractChanged: EventEmitter<Contract> = new EventEmitter<Contract>();

    isLoading = false;
    helper = helper;

    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    uploading: any = {
        files_mesagerie_intern: false,
        files_mesagerie_notita: false,
    };

    // mesagerie
    selectedMessagingToggle = 'internalMsg';
    editorConfig!: AngularEditorConfig;

    sendMessageInternForm!: FormGroup;
    sendMessageNotitaForm!: FormGroup;

    // colleagues functionality
    separatorKeysCodes: number[] = [ENTER, COMMA];
    filteredColleagues!: any;
    colleagues: User[] = [];
    allColleagues: User[] = [];
    colleaguesCtrl = new FormControl('');
    @ViewChild('colleagueInput') colleagueInput!: ElementRef<HTMLInputElement>;

    // Subscription to track our sabloane added from the button below messaging component
    private contentSubscription!: Subscription;

    constructor(
        private fb: FormBuilder,
        private notificationService: NotificationService,
        private beneficiariService: BeneficiariService,
        private userService: UsersService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private appFilesService: AppFilesService,
        private contracteProgramariService: ContracteProgramariService,
        public toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.getUsersActive();

        this.sendMessageInternForm = this.fb.group({
            type: new FormControl(2),
            message: new FormControl('', [Validators.required]),
            colleagues: new FormControl(this.colleagues, requireMinOneColleagueValidator()),
            files: this.fb.array([]),
        })

        this.sendMessageNotitaForm = this.fb.group({
            type: new FormControl(3),
            message: new FormControl('', [Validators.required]),
            files: this.fb.array([]),
        })

        this.filteredColleagues = this.colleaguesCtrl.valueChanges.pipe(
            startWith(null),
            map((value: string | null) =>
                value ? this._colleaguesFilter(value) : this.allColleagues.slice()
            )
        );
    }

    ngOnDestroy(): void {
        if (this.contentSubscription) {
            this.contentSubscription.unsubscribe();
        }
    }

    onMessagingChange(event: MatButtonToggleChange) {
        this.selectedMessagingToggle = event.value;
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    // colleagues section
    private _colleaguesFilter(value: string | User): User[] {
        let filterValue = '';
        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else {
            filterValue = value.name.toLowerCase();
        }

        return this.allColleagues.filter((colleague) =>
            colleague.name.toLowerCase().includes(filterValue)
        );
    }

    addColleague(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        if (value) {
            const foundOption = this.allColleagues.find(
                (user) => this.normalizeString(user.name) === this.normalizeString(value)
            );
            if (foundOption) {
                this.colleagues.push(foundOption);
                this.sendMessageInternForm.controls['colleagues'].setValue([...this.colleagues]);
                this.sendMessageInternForm.controls['colleagues'].updateValueAndValidity();
            }
        }

        event.chipInput!.clear();
        this.colleaguesCtrl.setValue(null);
    }

    removeColleague(colleague: User): void {
        const index = this.colleagues.indexOf(colleague);

        if (index >= 0) {
            this.colleagues.splice(index, 1);
            // Update the form control to reflect changes and re-validate
            this.sendMessageInternForm.controls['colleagues'].setValue([...this.colleagues]);
            this.sendMessageInternForm.controls['colleagues'].updateValueAndValidity();
        }
    }

    selectedColleagues(event: MatAutocompleteSelectedEvent): void {
        const selectedOption = this.allColleagues.find(
            (colleague) => colleague.name === event.option.viewValue
        );
        if (
            selectedOption &&
            !this.colleagues.find(
                (colleague) => colleague.id_user === selectedOption.id_user
            )
        ) {
            this.colleagues.push(selectedOption);
            this.colleagueInput.nativeElement.value = '';
            this.colleaguesCtrl.setValue(null);
            // Update the form control to reflect changes and re-validate
            this.sendMessageInternForm.controls['colleagues'].setValue([...this.colleagues]);
            this.sendMessageInternForm.controls['colleagues'].updateValueAndValidity();
        }
    }

    getUsersActive() {
        this.isLoading = true;
        this.userService
            .getUsersActive()
            .pipe(
                map((result: any) => result.data.map(
                    (user: any) => {
                        return {
                            id_user: user.id_user,
                            name: user.name,
                            email: user.email
                        }
                    }
                )),
                finalize(() => (this.isLoading = false))
            )
            .subscribe((response) => {
                this.allColleagues = response;
            });
    }

    // files section
    addFileIntern(event: any, key: string) {
        event.preventDefault();
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Verifică dacă fișierul există deja în lista de fișiere încărcate
            const formFiles = this.sendMessageInternForm.get('files')?.value;
            const fileExists = formFiles.some(
                (uploadedFile: FileUpload) => uploadedFile.original_name === file.name
            );

            if (fileExists) {
                this.openToast({
                    title: 'Atenție',
                    message: `Fișierul "${file.name}" este deja încărcat.`,
                    type: 'warning',
                });
                continue; // Sari peste acest fișier și continuă cu următorul
            }

            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.beneficiariService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (response.status_code == 200 && typeof response.data !== 'undefined') {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.sendMessageInternForm.get('files') as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;

                            this.openToast({ title: this.successTitle, message: helper.dialogConfig.generalMessages.fileSuccessfullyUploaded, type: this.successIcon });
                        }
                    } else {
                        let errorMessage = environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }

        // Reset the input value to allow re-uploading the same file
        inputElement.value = '';
    }

    addFileNotita(event: any, key: string) {
        event.preventDefault();
        const inputElement = event.target as HTMLInputElement;
        if (!inputElement.files) {
            return;
        }

        const files = inputElement.files;

        // Iterate through the selected files and append them to the 'files' array in the form
        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Verifică dacă fișierul există deja în lista de fișiere încărcate
            const formFiles = this.sendMessageNotitaForm.get('files')?.value;
            const fileExists = formFiles.some(
                (uploadedFile: FileUpload) => uploadedFile.original_name === file.name
            );

            if (fileExists) {
                this.openToast({
                    title: 'Atenție',
                    message: `Fișierul "${file.name}" este deja încărcat.`,
                    type: 'warning',
                });
                continue; // Sari peste acest fișier și continuă cu următorul
            }

            this.uploading[key] = true;

            const formData = new FormData();
            formData.append('file', file, file.name);

            this.beneficiariService.fileUpload(formData).subscribe({
                next: (result: any) => {
                    let response =
                        typeof result.status_code !== 'undefined' ? result : result.error;
                    if (typeof response.status_code !== 'undefined') {
                        if (response.status_code == 200 && typeof response.data !== 'undefined') {
                            // Handle the uploaded file by adding it to the 'files' array in the form
                            const files = this.sendMessageNotitaForm.get('files') as FormArray;
                            files.push(new FormControl(result.data));

                            this.uploading[key] = false;

                            this.openToast({ title: this.successTitle, message: helper.dialogConfig.generalMessages.fileSuccessfullyUploaded, type: this.successIcon });
                        }
                    } else {
                        let errorMessage = environment.config.customNotifications.generalMessages.error;
                        this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
                        this.isLoading = false;
                        this.uploading[key] = false;
                    }
                },
                error: (result: any) => {
                    this.isLoading = false;
                    this.uploading[key] = false;
                    this.onHttpError(result.error);
                },
            });
        }

        // Reset the input value to allow re-uploading the same file
        inputElement.value = '';
    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({ title: this.errorTitle, message: msg, type: this.errorType, });
                } else {
                    this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
                }
            });
        } else {
            let errorMessage = environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(this.errorTitle, errorMessage, this.errorIcon);
        }
    }

    removeFileIntern(index: number) {
        const files = this.sendMessageInternForm.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    removeFileNota(index: number) {
        const files = this.sendMessageNotitaForm.get('files') as FormArray;
        if (index > -1 && index < files.length) {
            files.removeAt(index);
        }
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }

    downloadTempFile(uploadedFile: FileUpload) {
        this.isLoading = true;
        const fullPath = this.beneficiariService.tempFileDownloadUrl;

        const payload = {
            fileName: uploadedFile.filename,
            originalName: uploadedFile.original_name,
        }
        this.appFilesService.postDownloadFile(fullPath, payload)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Download doc error: ', err);
                this.isLoading = false;
            })
    }

    resetAllForms() {
        if (this.sendMessageInternForm.value) {
            this.sendMessageInternForm.get('type')?.setValue('2');
            this.sendMessageInternForm.get('message')?.setValue('');
            this.sendMessageInternForm.get('message')?.markAsUntouched();
            this.colleagues = [];
            this.sendMessageInternForm.get('colleagues')?.setValue(this.colleagues);

            const filesFormArray = this.sendMessageInternForm.get('files') as FormArray;
            filesFormArray.clear();
            this.sendMessageInternForm.clearValidators();
        }

        if (this.sendMessageNotitaForm.value) {
            this.sendMessageNotitaForm.get('type')?.setValue('3');
            this.sendMessageNotitaForm.get('message')?.setValue('');
            this.sendMessageNotitaForm.get('message')?.markAsUntouched();

            const filesFormArray = this.sendMessageNotitaForm.get('files') as FormArray;
            filesFormArray.clear();
            this.sendMessageNotitaForm.clearValidators();
        }
    }

    submitMessage(section: any) {
        this.isLoading = true;

        var newMessage;
        switch (section) {
            case 'intern':
                newMessage = this.sendMessageInternForm
                    .value as RequestMessageInternPayload;
                break;

            case 'nota':
                newMessage = this.sendMessageNotitaForm
                    .value as RequestMessageNotitaPayload;
                break;
        }

        newMessage = { contract_id: this.contract.id, ...newMessage };

        this.contracteProgramariService
            .postMessage(this.contract.entity.id, newMessage)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (res) => {
                    this.isLoading = true;
                    // Commented the function below so that we dont have a popup when the loading spinner is active
                    // this.notificationService.warningSwal(
                    //     this.successTitle,
                    //     'Mesaj trimis cu succes!',
                    //     this.successIcon
                    // );
                    // Reset forms and refresh data
                    this.resetAllForms();
                    this.contractChanged.emit();
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
